import { useEffect, useState } from 'react';
import { X, ShoppingCart, Loader2, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { Product } from '../../store/useProductStore';
import SaudiRiyal from '../icons/SaudiRiyal';
import { CustomerReviewsSection } from '../customer-reviews';
import { normalizeMediaUrl, onCdnImageError } from '../../lib/mediaUrl';
import * as S from './styles';

function formatPrepTime(min: number): string {
  if (min <= 0) return '';
  if (min === 60) return 'ساعة';
  if (min === 120) return 'ساعتين';
  if (min === 90) return 'ساعة ونصف';
  if (min % 60 === 0 && min > 0) return `${min / 60} ساعات`;
  if (min >= 60) return `${Math.floor(min / 60)} ساعة و${min % 60} دقيقة`;
  return `${min} دقيقة`;
}

interface ProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: Product) => Promise<void>;
}

const ProductModal = ({ product, isOpen, onClose, onAddToCart }: ProductModalProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);

  // إغلاق الـ modal عند الضغط على ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // منع التمرير في الخلفية
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // إغلاق الصورة الكاملة عند الضغط على ESC
  useEffect(() => {
    if (!isImageFullscreen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsImageFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // إعادة overflow إلى الحالة السابقة (مخفي إذا كان modal المنتج مفتوحاً)
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isImageFullscreen, isOpen]);

  if (!isOpen || !product) return null;

  const isSoldOut = product.soldOut === true;
  const isNew = product.new === true;
  const isPreOrder = product.preOrder === true;

  const handleAddToCart = async () => {
    if (isSoldOut || isAdding) return;
    
    setIsAdding(true);
    try {
      await onAddToCart(product);
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageClick = () => {
    setIsImageFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsImageFullscreen(false);
  };

  return (
    <>
      <S.ModalOverlay onClick={handleBackdropClick}>
        <S.ModalContent onClick={(e) => e.stopPropagation()}>
          <S.ModalCloseButton onClick={onClose}>
            <X size={20} />
          </S.ModalCloseButton>

          <S.ModalImageContainer>
            <S.ModalImage 
              src={normalizeMediaUrl(product.image)} 
              alt={product.title}
              onClick={handleImageClick}
              style={{ cursor: 'pointer' }}
              onError={onCdnImageError}
            />
            {isNew && <S.ModalNewBadge>جديدنا</S.ModalNewBadge>}
            {isPreOrder && <S.ModalPreOrderBadge>حجز مسبق</S.ModalPreOrderBadge>}
          </S.ModalImageContainer>

        <S.ModalBody>
          <S.ModalTitle>{product.title}</S.ModalTitle>

          {product.description && (
            <S.ModalDescription>{product.description}</S.ModalDescription>
          )}

          {product.prepTimeMinutes != null && product.prepTimeMinutes > 0 && (
            <S.ModalPrepTime>
              <Clock size={14} /> مدة التحضير: {formatPrepTime(product.prepTimeMinutes)}
            </S.ModalPrepTime>
          )}

          <S.ModalPriceContainer>
            <S.ModalCurrentPrice>
              {product.price.toFixed(2)} <SaudiRiyal size={18} style={{ marginRight: '4px' }} />
            </S.ModalCurrentPrice>
            {product.originalPrice && (
              <S.ModalOldPrice>
                {product.originalPrice.toFixed(2)} <SaudiRiyal size={14} style={{ marginRight: '2px' }} />
              </S.ModalOldPrice>
            )}
          </S.ModalPriceContainer>

          <CustomerReviewsSection
              key={product.id}
              productId={product.id}
              productName={product.title}
              variant="embedded"
            />

          {isSoldOut ? (
            <S.ModalSoldOutButton disabled>
              نفذ المخزون
            </S.ModalSoldOutButton>
          ) : (
            <S.ModalAddButton onClick={handleAddToCart} disabled={isAdding}>
              {isAdding ? (
                <>
                  <Loader2 size={18} className="spinner" />
                  جاري الإضافة...
                </>
              ) : (
                <>
                  إضافة للسلة
                  <ShoppingCart size={16} strokeWidth={2.25} />
                </>
              )}
            </S.ModalAddButton>
          )}
        </S.ModalBody>
      </S.ModalContent>
    </S.ModalOverlay>

    {isImageFullscreen && (
      <S.FullscreenImageOverlay onClick={handleCloseFullscreen}>
        <S.FullscreenImageCloseButton onClick={handleCloseFullscreen}>
          <X size={24} />
        </S.FullscreenImageCloseButton>
        <S.FullscreenImageContainer onClick={(e) => e.stopPropagation()}>
          <S.FullscreenImage src={normalizeMediaUrl(product.image)} alt={product.title} onError={onCdnImageError} />
        </S.FullscreenImageContainer>
      </S.FullscreenImageOverlay>
    )}
    </>
  );
};

export default ProductModal;
